import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";

import { ApiError, type ApiIssue } from "../api/errors";
import { productsApi } from "../api/products";
import type {
  AvailableAction,
  DraftValidation,
  LifecycleResult,
  Product,
  Revision,
} from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import { ConfirmDialog } from "./ConfirmDialog";
import { useProductRefresh } from "./useProduct";

/** The lifecycle transitions this panel can offer.
 *
 * Which of them appear is decided entirely by the backend's available_actions.
 * Nothing here re-derives eligibility from the product's status: the rules that
 * actually run live in PublishService, and a second copy in the browser would
 * eventually offer a button that is guaranteed to fail.
 */
type LifecycleAction = Extract<
  AvailableAction,
  "publish" | "publish_changes" | "unpublish" | "republish" | "archive"
>;

const LIFECYCLE_ORDER: LifecycleAction[] = [
  "publish",
  "publish_changes",
  "unpublish",
  "republish",
  "archive",
];

/**
 * The two actions that ask nothing before running.
 *
 * Opening a draft is idempotent — the backend returns the existing one — and
 * validating only reads. Neither changes what a customer sees, so a
 * confirmation would be ceremony. They live here rather than on their own
 * screens because this component is the one place that turns the backend's
 * available_actions into buttons; a second owner is how "Start editing" ends up
 * on one screen and missing on another.
 */
type DirectAction = Extract<AvailableAction, "open_draft" | "validate_draft">;

const DIRECT_LABELS: Record<DirectAction, { idle: string; busy: string }> = {
  // "Resume" is the operator's word for it: the product is mid-flight and this
  // picks it back up. It never creates a second draft.
  open_draft: { idle: "Resume editing", busy: "Opening…" },
  validate_draft: { idle: "Check the product", busy: "Checking…" },
};

interface Copy {
  button: string;
  title: string;
  confirm: string;
  destructive?: boolean;
  body: (context: { live: Revision | null; draft: Revision | null }) => ReactNode;
}

const COPY: Record<LifecycleAction, Copy> = {
  publish: {
    button: "Publish",
    title: "Publish this product?",
    confirm: "Publish",
    body: ({ draft }) => (
      <>
        The complete draft
        {draft ? ` (revision ${draft.revision_number})` : ""} becomes the live
        revision and customers can see this product immediately. Everything in
        the draft is published together — details, colors and images — or
        nothing is.
      </>
    ),
  },
  publish_changes: {
    button: "Publish changes",
    title: "Publish these changes?",
    confirm: "Publish changes",
    body: ({ live, draft }) => (
      <>
        Draft revision {draft?.revision_number} replaces revision{" "}
        {live?.revision_number} as the live one. Customers keep seeing revision{" "}
        {live?.revision_number} until this succeeds — if anything fails, nothing
        changes and the current revision stays live.
      </>
    ),
  },
  unpublish: {
    button: "Unpublish",
    title: "Unpublish this product?",
    confirm: "Unpublish",
    destructive: true,
    body: () => (
      <>
        The product disappears from the public catalog straight away. Nothing is
        deleted: the revision it was serving, its colors, images and history are
        all kept, and it can be republished later.
      </>
    ),
  },
  republish: {
    button: "Republish",
    title: "Republish this product?",
    confirm: "Republish",
    body: ({ live }) => (
      <>
        The revision this product kept
        {live ? ` (revision ${live.revision_number})` : ""} becomes visible to
        customers again. No new revision is created.
      </>
    ),
  },
  archive: {
    button: "Archive",
    title: "Archive this product?",
    confirm: "Archive",
    destructive: true,
    body: () => (
      <>
        The product disappears from the public catalog and from the normal admin
        list — it stays reachable through the archived filter. Every revision,
        color, image and history record is kept. Unarchiving is not available in
        this phase.
      </>
    ),
  },
};

export function LifecycleActions({
  productId,
  product,
  live,
  draft,
  availableActions,
  initialAction = null,
  onInitialActionConsumed,
}: {
  productId: string;
  product: Product;
  live: Revision | null;
  draft: Revision | null;
  availableActions: AvailableAction[];
  /** An action deep-linked from the Products List, e.g. ?action=archive.
   *  It opens this component's own confirmation — the list never performs a
   *  lifecycle call itself, so there is exactly one implementation of each. */
  initialAction?: LifecycleAction | null;
  onInitialActionConsumed?: () => void;
}) {
  const { can } = useAuth();
  const refresh = useProductRefresh(productId);
  const [pending, setPending] = useState<LifecycleAction | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  /** The verdict from a Check the product press, when one has been made. */
  const [checked, setChecked] = useState<DraftValidation | null>(null);

  // A deep-linked action opens its confirmation once, and only if the backend
  // actually offers it. An ?action= for something this product cannot do is
  // ignored rather than shown as a button that would be refused.
  useEffect(() => {
    if (!initialAction) return;
    if (availableActions.includes(initialAction)) setPending(initialAction);
    onInitialActionConsumed?.();
  }, [initialAction]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useMutation({
    mutationFn: async (action: LifecycleAction): Promise<LifecycleResult> => {
      // Versions are read at the moment of the request from the loaded product,
      // so a confirmation left open while something else changed is rejected by
      // the server rather than silently overwriting it.
      switch (action) {
        case "publish":
        case "publish_changes": {
          if (!draft) throw new Error("no draft to publish");
          // Publish cannot bypass validation, and deliberately not because of a
          // check made here: PublishService validates the draft as part of
          // publishing and refuses with a `validation` error carrying the
          // blocking issues, which LifecycleError renders below. A pre-flight
          // check in the browser would be a second copy of a rule that already
          // runs, free to disagree with it — the exact failure this component's
          // header warns about.
          return productsApi.publish(productId, product.version, draft.version);
        }
        case "unpublish":
          return productsApi.unpublish(productId, product.version);
        case "republish":
          return productsApi.republish(productId, product.version);
        case "archive":
          return productsApi.archive(productId, product.version);
      }
    },
    onSuccess: async (result, action) => {
      setError(null);
      setPending(null);
      setChecked(null);
      setSuccess(successMessage(action, result));
      // Everything the page shows about lifecycle is now stale — including the
      // action list itself, which is why the whole product is reloaded rather
      // than patched locally.
      await refresh();
    },
    onError: async (caught) => {
      setError(caught);
      setPending(null);
      setSuccess(null);
      // A conflict means the local versions are behind; reload so the next
      // attempt carries current ones. The request is never resent on its own.
      await refresh();
    },
  });

  /** Resume and Validate. Neither confirms, and neither can change a customer's
   *  view, so both run on the press. */
  const direct = useMutation({
    mutationFn: async (action: DirectAction) => {
      if (action === "open_draft") {
        await productsApi.openDraft(productId);
        return null;
      }
      return productsApi.validateDraft(productId);
    },
    onSuccess: async (result, action) => {
      setError(null);
      if (action === "validate_draft") {
        setChecked(result);
        setSuccess(null);
      } else {
        setChecked(null);
        setSuccess("Draft open. Your changes stay out of the shop until you publish.");
      }
      await refresh();
    },
    onError: async (caught) => {
      setError(caught);
      setSuccess(null);
      await refresh();
    },
  });

  // The backend says which actions this PRODUCT allows; capabilities say which
  // this ADMINISTRATOR may take. Both narrow the same list, and neither is the
  // authorization — every one of these calls is refused server-side on its own
  // authority. Offering a button that can only answer 403 is what this avoids.
  const actions = can(CAP.productsPublish)
    ? LIFECYCLE_ORDER.filter((action) => availableActions.includes(action))
    : [];
  const directActions = can(CAP.productsWrite)
    ? (["open_draft", "validate_draft"] as DirectAction[]).filter((action) =>
        availableActions.includes(action),
      )
    : [];
  const busy = run.isPending || direct.isPending;

  return (
    <div className="stack stack--tight">
      <div className="row row--wrap">
        {directActions.map((action) => (
          <button
            key={action}
            type="button"
            className="btn"
            disabled={busy}
            data-testid={`lifecycle-${action}`}
            onClick={() => {
              setSuccess(null);
              setError(null);
              direct.mutate(action);
            }}
          >
            {direct.isPending && direct.variables === action
              ? DIRECT_LABELS[action].busy
              : DIRECT_LABELS[action].idle}
          </button>
        ))}
        {actions.map((action) => {
          const copy = COPY[action];
          return (
            <button
              key={action}
              type="button"
              className={
                action === "publish" || action === "publish_changes"
                  ? "btn btn--primary"
                  : "btn"
              }
              disabled={busy}
              data-testid={`lifecycle-${action}`}
              onClick={() => {
                setSuccess(null);
                setError(null);
                setChecked(null);
                setPending(action);
              }}
            >
              {copy.button}
            </button>
          );
        })}
      </div>

      {success ? (
        <div className="notice notice--success" role="status">
          {success}
        </div>
      ) : null}

      {checked ? (
        <div
          className={
            checked.publishable ? "notice notice--success" : "notice notice--error"
          }
          role="status"
          data-testid="validation-verdict"
        >
          {checked.publishable ? (
            "Everything needed is in place. This product is ready to publish."
          ) : (
            <>
              <strong>These have to be fixed before publishing</strong>
              <IssueList issues={checked.blocking} />
            </>
          )}
        </div>
      ) : null}

      {error ? <LifecycleError error={error} /> : null}

      {pending ? (
        <ConfirmDialog
          title={COPY[pending].title}
          confirmLabel={COPY[pending].confirm}
          destructive={COPY[pending].destructive}
          busy={run.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => run.mutate(pending)}
        >
          {COPY[pending].body({ live, draft })}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function successMessage(action: LifecycleAction, result: LifecycleResult): string {
  switch (action) {
    case "publish":
      return `Published revision ${result.revision_number ?? ""}. Customers can see this product now.`;
    case "publish_changes":
      return `Revision ${result.revision_number ?? ""} is now live. The previous revision is kept as history.`;
    case "unpublish":
      return "Product unpublished. It is hidden from customers and nothing was deleted.";
    case "republish":
      return `Republished revision ${result.revision_number ?? ""}. It is visible to customers again.`;
    case "archive":
      return "Product archived. Find it again using the archived filter.";
  }
}

/** Lifecycle failures each need a different sentence, because each implies a
 *  different next step for the administrator. */
function LifecycleError({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) {
    return (
      <div className="notice notice--error" role="alert">
        That action could not be completed.
      </div>
    );
  }

  if (error.kind === "validation") {
    return (
      <div className="notice notice--error" role="alert">
        <strong>This product cannot go live yet.</strong>
        <IssueList issues={error.issues} />
      </div>
    );
  }

  if (error.kind === "conflict") {
    const code = error.code;
    const explanation =
      code === "stale_product_version" || code === "stale_draft_version"
        ? "This product changed somewhere else, so nothing was applied. The latest state has been reloaded — check it and try again."
        : code === "not_published"
          ? "This product is not published, so it cannot be unpublished. The current state has been reloaded."
          : code === "already_archived"
            ? "This product is already archived."
            : code === "not_unpublished"
              ? "Only an unpublished product can be republished. The current state has been reloaded."
              : error.userMessage;
    return (
      <div className="notice notice--warning" role="alert">
        {explanation}
      </div>
    );
  }

  return (
    <div className="notice notice--error" role="alert">
      {error.userMessage}
    </div>
  );
}

function IssueList({ issues }: { issues: ApiIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <ul>
      {issues.map((issue, index) => (
        <li key={`${issue.code}-${issue.entity ?? index}`}>
          {issue.message}{" "}
          <span className="mono">{issue.code}</span>
        </li>
      ))}
    </ul>
  );
}
